<template>
  <div id="app" class="page">
    <template v-if="shouldShowNavigation">
      <button id="open-sidebar-button" class="sidebar-toggle" @click="openSidebar">
        <font-awesome-icon :icon="['fas', 'bars']" />
      </button>
      <button :class="['sidebar-collapse', sidebarCollapsed && 'sidebar-collapse_closed']" @click="collapseSidebar">
        <font-awesome-icon :icon="['fas', sidebarCollapsed ? 'chevron-circle-right' : 'chevron-circle-left']" />
      </button>
      <aside class="sidebar" :class="sidebarClasses">
        <button id="close-sidebar-button" class="sidebar__close" @click="closeSidebar">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
        <logo class="page__logo" id="logo"></logo>
        <main-menu id="main-menu"></main-menu>
        <last-updated-at test-id="last-updated" v-if="shouldShowLastUpdated" id="pipeline-last-updated">
        </last-updated-at>
      </aside>
    </template>
    <main class="page__main">
      <main-header v-if="shouldShowNavigation"></main-header>
      <header-banner></header-banner>
      <router-view class="page__content"></router-view>
      <contact-support></contact-support>
    </main>
    <div class="page__loader" v-if="isFetchingPermissions">
      <voxy-spinner color="#ffffff" :size="48" :stroke="2"></voxy-spinner>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { VoxySpinner } from '@voxy-team/vue-voxy';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import Logo from 'components/logo/Logo';
import MainHeader from '@/components/MainHeader';
import MainMenu from '@/components/MainMenu';
import LastUpdatedAt from '@/components/LastUpdatedAt';
import dom from './utils/dom';
import HeaderBanner from './components/HeaderBanner';
import ContactSupport from './components/ContactSupport';
import { FAVICONS, PAGE_TITLES } from './constants';

export const IS_SIDEBAR_COLLAPSED_KEY = 'sidebar-is-collapsed';

export default {
  name: 'App',

  components: {
    HeaderBanner,
    FontAwesomeIcon,
    LastUpdatedAt,
    Logo,
    MainHeader,
    MainMenu,
    ContactSupport,
    VoxySpinner,
  },

  created() {
    this.nowDateIntervalId = setInterval(() => this.updateNowDate(), 1000 * 60);
    this.updateFavicon(this.isEnGenDomain);
    this.updatePageTitle(this.isEnGenDomain);
  },

  beforeDestroy() {
    if (this.nowDateIntervalId) {
      clearInterval(this.nowDateIntervalId);
      this.nowDateIntervalId = null;
    }
  },

  data() {
    return {
      sidebarIsOpen: false,
      nowDateIntervalId: null,
      isEnGenDomain: window.location.hostname === process.env.ENGEN_HOSTNAME,
    };
  },

  computed: {
    ...mapGetters(['isFetchingPermissions', 'isAuthenticated']),

    ...mapGetters('localSettings', ['isSidebarCollapsed']),

    shouldShowNavigation() {
      return this.isAuthenticated && this.$route.name !== 'login';
    },

    shouldShowLastUpdated() {
      const availableRoutesNames = [
        'learners',
        'reports.session',
        'reports.units_and_courses.courses',
        'reports.units_and_courses.units',
        'reports.live_instruction.group_class',
      ];
      return this.isAuthenticated && availableRoutesNames.includes(this.$route.name);
    },

    sidebarClasses() {
      return [this.sidebarIsOpen && 'sidebar_open', this.sidebarCollapsed && 'sidebar_collapse'];
    },

    sidebarCollapsed: {
      get() {
        return this.isSidebarCollapsed;
      },

      set(value) {
        this.setSetting({ key: IS_SIDEBAR_COLLAPSED_KEY, value });
      },
    },
  },

  methods: {
    ...mapActions({
      updateNowDate: 'UPDATE_NOW_DATE',
    }),

    ...mapMutations('localSettings', {
      setSetting: 'SET_SETTING',
    }),

    openSidebar() {
      this.sidebarIsOpen = true;
    },
    closeSidebar() {
      this.sidebarIsOpen = false;
    },
    toggleSidebar() {
      this.sidebarIsOpen = !this.sidebarIsOpen;
    },

    collapseSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    },

    updateFavicon(isEnGenDomain) {
      if (isEnGenDomain) dom.appendFavicon(FAVICONS.engen);
    },

    updatePageTitle(isEnGenDomain) {
      if (!isEnGenDomain) dom.updatePageTitle(PAGE_TITLES.voxy);
      else dom.updatePageTitle(PAGE_TITLES.engen);
    },
  },

  watch: {
    $route() {
      this.closeSidebar();
    },
  },
};
</script>
<style lang="scss">
@import '~styles/tools/mixins';
@import '~styles/tools/variables';

.page {
  display: flex;
  width: 100%;
  height: 100%;

  .v-leave {
    display: none;
  }
}

.page__logo {
  align-self: flex-start;
  padding: 0 18px;
}

.page__content {
  @include container();

  padding-top: 2.5rem;
  overflow-x: hidden;
}

.page__loader {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $night-rider-grey;
  z-index: 1020;
}

.page__main {
  flex: auto;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

.sidebar {
  position: fixed;
  top: 0;
  min-height: 100vh;
  background-color: $default-lighter-grey;
  padding: 44px 0 15px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  flex: 0 0 250px;
  transform: translateX(-100%);
  transition: transform 0.26s;
  z-index: 1010;

  &_open {
    transform: translateX(0);
    max-width: 250px;
  }

  &__close {
    font-size: 20px;
    position: absolute;
    top: 10px;
    right: 13px;
    cursor: pointer;
    color: white;
    opacity: 0.54;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
    }
  }

  @include respond-to(desktop) {
    flex: 0 0 14rem;
    position: relative;
    padding: 15px 0;
    transform: none;

    &_collapse {
      position: fixed;
      transform: translateX(-100%);
    }

    &__close {
      display: none;
    }
  }
}

.sidebar-toggle {
  font-size: 28px;
  position: fixed;
  z-index: 1010;
  color: white;
  top: 20px;
  left: 22px;
  cursor: pointer;

  &:hover {
    color: $default-blue;
  }

  @include respond-to(desktop) {
    display: none;
  }
}

.sidebar-collapse {
  display: none;
  font-size: 20px;
  position: fixed;
  z-index: 1020;
  color: white;
  top: 62px;
  left: 214px;
  cursor: pointer;
  transition: 0.26s;

  &:hover {
    color: $default-blue;
  }

  &_closed {
    left: -7px;
  }

  @include respond-to(desktop) {
    display: inline-block;
  }
}
</style>
